// SCR-STAFF-803 (AP-CMP-02؛ US-CMP-030، 031): معرفان (شخص، سازمان، کارکنان، بالینی) با نرخ کمیسیون و پنجره ماه، انتساب مراجعه‌کننده و گزارش
// کمیسیون فروشندگان و معرفان. کمیسیون معرف بالینی پیش‌فرض غیرفعال است و فعال‌سازی فقط با تأیید مالک و پذیرش مسئولیت (BR-CMP-032؛ PHX-CMP-0209).
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import {
  unwrap,
  type CmpCommissionSummaryRow,
  type CmpReferrerKind,
  type CmpReferrerRequest,
  type CmpReferrerView,
  type CmpShareMethod,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { PatientPicker } from '../patients/PatientPicker';
import { WriteNotice } from '../settings/common';
import { compensationKeys, LIST_SEPARATOR, toBasisPoints } from './common';
import { currentJalaliMonth } from './ShareReportPage';

export const SCR_STAFF_803 = 'SCR-STAFF-803';

export const REFERRER_KINDS: readonly CmpReferrerKind[] = ['Person', 'Organization', 'Staff', 'Clinical'];

export function ReferrersPage() {
  const { t } = useTranslation('cmp');
  const access = useAccess();
  const write = useWriteAccess('cmp.referrer.manage');
  const [tab, setTab] = useState<'referrers' | 'commissions'>('referrers');
  const [creating, setCreating] = useState(false);
  const canManage = access.can('cmp.referrer.manage') && write.canWrite;

  return (
    <ListPage
      screenId={SCR_STAFF_803}
      titleKey="cmp:referrers.title"
      subtitleKey="cmp:referrers.subtitle"
      actions={
        access.can('cmp.referrer.manage') ? (
          <Button
            variant="contained"
            onClick={() => setCreating(true)}
            disabled={!write.canWrite}
            data-testid="referrer-add"
          >
            {t('cmp:referrers.add')}
          </Button>
        ) : undefined
      }
      filters={
        <Stack spacing={1}>
          {access.can('cmp.referrer.manage') && <WriteNotice write={write} />}
          <Tabs value={tab} onChange={(_, value: 'referrers' | 'commissions') => setTab(value)}>
            <Tab
              value="referrers"
              label={t('cmp:referrers.tab_referrers')}
              data-testid="referrers-tab-list"
            />
            {access.can('cmp.report.view') && (
              <Tab
                value="commissions"
                label={t('cmp:referrers.tab_commissions')}
                data-testid="referrers-tab-commissions"
              />
            )}
          </Tabs>
        </Stack>
      }
    >
      {tab === 'referrers' ? <ReferrersTable canManage={canManage} /> : <CommissionsTable />}
      {creating && <ReferrerDialog onClose={() => setCreating(false)} />}
    </ListPage>
  );
}

function ReferrersTable({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const [commission, setCommission] = useState<CmpReferrerView | null>(null);
  const [assigning, setAssigning] = useState<CmpReferrerView | null>(null);
  const referrers = useQuery({
    queryKey: compensationKeys.referrers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/referrers')),
  });

  const columns: DataTableColumn<CmpReferrerView>[] = [
    { id: 'name', headerKey: 'cmp:referrers.columns.name', cell: (row) => row.name },
    {
      id: 'kind',
      headerKey: 'cmp:referrers.columns.kind',
      cell: (row) => t(`cmp:referrer_kind.${row.kind}`),
    },
    {
      id: 'rate',
      headerKey: 'cmp:referrers.columns.rate',
      cell: (row) =>
        row.method === 'Percent' ? (
          t('cmp:detail.rate', { rate: faDigits(Number(row.rateBasisPoints ?? 0) / 100) })
        ) : row.amount ? (
          <Money amountMinor={Number(row.amount.amount)} />
        ) : (
          '—'
        ),
    },
    {
      id: 'window',
      headerKey: 'cmp:referrers.columns.window',
      cell: (row) =>
        t('cmp:referrers.months', {
          count: Number(row.windowMonths),
          value: faDigits(Number(row.windowMonths)),
        }),
    },
    {
      id: 'commission',
      headerKey: 'cmp:referrers.columns.commission',
      cell: (row) => (
        <span data-testid={`referrer-commission-${row.id}`}>
          <StatusChip
            tone={row.commissionEnabled ? 'active' : 'inactive'}
            labelKey={row.commissionEnabled ? 'cmp:referrers.enabled' : 'cmp:referrers.disabled'}
          />
        </span>
      ),
    },
    {
      id: 'patients',
      headerKey: 'cmp:referrers.columns.patients',
      cell: (row) =>
        row.patients.length
          ? row.patients.map((patient) => patient.patientName ?? '—').join(LIST_SEPARATOR)
          : '—',
    },
    {
      id: 'earned',
      headerKey: 'cmp:referrers.columns.earned',
      cell: (row) => <Money amountMinor={Number(row.earned.amount)} />,
      align: 'right',
    },
    {
      id: 'actions',
      headerKey: 'cmp:referrers.columns.actions',
      cell: (row) =>
        canManage ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setCommission(row)} data-testid={`referrer-toggle-${row.id}`}>
              {t(row.commissionEnabled ? 'cmp:referrers.disable' : 'cmp:referrers.enable')}
            </Button>
            <Button size="small" onClick={() => setAssigning(row)} data-testid={`referrer-assign-${row.id}`}>
              {t('cmp:referrers.assign')}
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable
        captionKey="cmp:referrers.caption"
        columns={columns}
        rows={referrers.data?.items}
        getRowId={(row) => row.id}
        isLoading={referrers.isPending}
        error={referrers.error}
        onRetry={() => void referrers.refetch()}
        emptyTitleKey="cmp:referrers.empty"
      />
      {commission && <CommissionDialog referrer={commission} onClose={() => setCommission(null)} />}
      {assigning && <AssignPatientDialog referrer={assigning} onClose={() => setAssigning(null)} />}
    </>
  );
}

const referrerSchema = z
  .object({
    name: zRequiredText(200),
    kind: z.string(),
    method: z.string(),
    rate: z.string(),
    amount: z.number().nullable(),
    windowMonths: z.string(),
    iban: z.string(),
    note: z.string(),
  })
  .superRefine((form, ctx) => {
    if (
      form.method === 'Percent' &&
      !(form.rate.trim() !== '' && Number(form.rate) > 0 && Number(form.rate) <= 100)
    ) {
      ctx.addIssue({ code: 'custom', path: ['rate'], message: 'cmp:simulator.rate_invalid' });
    }
    if (form.method === 'FixedPerCase' && !(form.amount && form.amount > 0)) {
      ctx.addIssue({ code: 'custom', path: ['amount'], message: 'common:validation.required' });
    }
    if (!(Number(form.windowMonths) >= 1 && Number(form.windowMonths) <= 120)) {
      ctx.addIssue({ code: 'custom', path: ['windowMonths'], message: 'cmp:referrers.window_invalid' });
    }
  });
type ReferrerForm = z.input<typeof referrerSchema>;

function ReferrerDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<ReferrerForm>({
    resolver: zodResolver(referrerSchema),
    defaultValues: {
      name: '',
      kind: 'Person',
      method: 'Percent',
      rate: '',
      amount: null,
      windowMonths: '12',
      iban: '',
      note: '',
    },
  });
  const kind = useWatch({ control, name: 'kind' });
  const method = useWatch({ control, name: 'method' });

  const save = useMutation({
    mutationFn: (form: ReferrerForm) => {
      const body: CmpReferrerRequest = {
        name: form.name.trim(),
        kind: form.kind as CmpReferrerKind,
        method: form.method as CmpShareMethod,
        rateBasisPoints: form.method === 'Percent' ? toBasisPoints(Number(form.rate)) : null,
        amount: form.method === 'FixedPerCase' ? { amount: form.amount ?? 0, currency: 'IRR' } : null,
        windowMonths: Number(form.windowMonths),
        iban: form.iban.replace(/\s/g, '') || null,
        note: form.note.trim() || null,
      };
      return unwrap(
        tenantApi.POST('/api/v1/compensation/referrers', {
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: compensationKeys.referrers(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['name', 'iban', 'rate', 'windowMonths'],
        codeFields: { 'PHX-CMP-0108': 'iban' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-803-FORM"
      titleKey="cmp:referrers.create_title"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <TextFieldRHF
        control={control}
        name="name"
        labelKey="cmp:referrers.name"
        required
        fullWidth
        slotProps={{ htmlInput: { 'data-testid': 'referrer-name' } }}
      />
      <SelectRHF
        control={control}
        name="kind"
        labelKey="cmp:referrers.kind"
        fullWidth
        options={REFERRER_KINDS.map((value) => ({ value, labelKey: `cmp:referrer_kind.${value}` }))}
      />
      {kind === 'Clinical' && (
        <DialogContentText data-testid="referrer-clinical-note" color="warning.main">
          {t('cmp:referrers.clinical_note')}
        </DialogContentText>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="method"
          labelKey="cmp:form.rule_method"
          fullWidth
          options={(['Percent', 'FixedPerCase'] as const).map((value) => ({
            value,
            labelKey: `cmp:method.${value}`,
          }))}
        />
        {method === 'Percent' ? (
          <TextFieldRHF
            control={control}
            name="rate"
            labelKey="cmp:form.rule_rate"
            fullWidth
            slotProps={{ htmlInput: { inputMode: 'decimal', 'data-testid': 'referrer-rate' } }}
          />
        ) : (
          <MoneyFieldRHF control={control} name="amount" labelKey="cmp:form.rule_amount" fullWidth />
        )}
        <TextFieldRHF
          control={control}
          name="windowMonths"
          labelKey="cmp:referrers.window"
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'numeric' } }}
        />
      </Stack>
      <TextFieldRHF
        control={control}
        name="iban"
        labelKey="cmp:form.iban"
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr' } }}
      />
      <TextFieldRHF control={control} name="note" labelKey="cmp:form.note" fullWidth />
      <DialogContentText variant="caption">{t('cmp:referrers.window_help')}</DialogContentText>
    </FormDialog>
  );
}

/** فعال یا غیرفعال کردن کمیسیون؛ برای معرف بالینی پذیرش مسئولیت الزامی است (AC-CMP-031.2). */
function CommissionDialog({ referrer, onClose }: { referrer: CmpReferrerView; onClose: () => void }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [accepted, setAccepted] = useState(false);
  const enabling = !referrer.commissionEnabled;
  const clinical = referrer.kind === 'Clinical';

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/referrers/{id}:set-commission', {
          params: { path: { id: referrer.id } },
          body: { enabled: enabling, acceptResponsibility: accepted, active: null },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: compensationKeys.referrers(session?.tenantId) });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={save.isPending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="referrer-commission-title"
    >
      <DialogTitle id="referrer-commission-title">
        {t(enabling ? 'cmp:referrers.enable_title' : 'cmp:referrers.disable_title', { name: referrer.name })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {enabling && clinical && (
            <>
              <DialogContentText>{t('cmp:referrers.clinical_warning')}</DialogContentText>
              <FormControlLabel
                control={
                  <Checkbox checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
                }
                label={t('cmp:referrers.accept_responsibility')}
                data-testid="referrer-accept"
              />
            </>
          )}
          {save.error ? <ErrorState error={save.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {t('cmp:detail.close')}
        </Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          data-testid="referrer-commission-confirm"
        >
          {t(enabling ? 'cmp:referrers.enable' : 'cmp:referrers.disable')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignPatientDialog({ referrer, onClose }: { referrer: CmpReferrerView; onClose: () => void }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const [touched, setTouched] = useState(false);

  const save = useMutation({
    mutationFn: (patientId: string) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/referrers/{id}/patients', {
          params: { path: { id: referrer.id } },
          body: { patientId, from: null },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: compensationKeys.referrers(session?.tenantId) });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={save.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="referrer-assign-title"
    >
      <DialogTitle id="referrer-assign-title">
        {t('cmp:referrers.assign_title', { name: referrer.name })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <PatientPicker
            labelKey="cmp:referrers.patient"
            value={patient}
            onChange={setPatient}
            required
            error={touched && !patient ? 'common:validation.required' : undefined}
            testId="referrer-patient"
          />
          <DialogContentText variant="caption">
            {t('cmp:referrers.assign_help', { months: faDigits(Number(referrer.windowMonths)) })}
          </DialogContentText>
          {referrer.patients.length > 0 && (
            <Stack spacing={0.5}>
              {referrer.patients.map((row) => (
                <span key={row.id}>
                  {row.patientName ?? '—'} · <JalaliDate value={row.from} /> –{' '}
                  <JalaliDate value={row.until} />
                </span>
              ))}
            </Stack>
          )}
          {save.error ? <ErrorState error={save.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {t('cmp:detail.close')}
        </Button>
        <Button
          variant="contained"
          disabled={save.isPending}
          data-testid="referrer-assign-confirm"
          onClick={() => {
            setTouched(true);
            if (patient) {
              save.mutate(patient.id);
            }
          }}
        >
          {t('cmp:referrers.assign')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** گزارش کمیسیون فروشندگان و معرفان (US-CMP-030). */
function CommissionsTable() {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const [period] = useState(currentJalaliMonth);
  const commissions = useQuery({
    queryKey: [...compensationKeys.commissions(session?.tenantId), period.from, period.to],
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/compensation/reports/commissions', { params: { query: period } })),
  });
  const columns: DataTableColumn<CmpCommissionSummaryRow>[] = [
    { id: 'name', headerKey: 'cmp:referrers.columns.name', cell: (row) => row.name ?? '—' },
    {
      id: 'type',
      headerKey: 'cmp:referrers.columns.beneficiary_type',
      cell: (row) => t(`cmp:beneficiary_type.${row.beneficiaryType}`),
    },
    { id: 'role', headerKey: 'cmp:ledger.columns.role', cell: (row) => t(`cmp:role.${row.role}`) },
    {
      id: 'lines',
      headerKey: 'cmp:report.columns.lines',
      cell: (row) => faDigits(Number(row.lines)),
      align: 'right',
    },
    {
      id: 'base',
      headerKey: 'cmp:report.columns.base',
      cell: (row) => <Money amountMinor={Number(row.base.amount)} />,
      align: 'right',
    },
    {
      id: 'earned',
      headerKey: 'cmp:referrers.columns.earned',
      cell: (row) => <Money amountMinor={Number(row.earned.amount)} />,
      align: 'right',
    },
    {
      id: 'pending',
      headerKey: 'cmp:referrers.columns.pending',
      cell: (row) => <Money amountMinor={Number(row.pending.amount)} />,
      align: 'right',
    },
  ];
  return (
    <DataTable
      captionKey="cmp:referrers.commissions_caption"
      columns={columns}
      rows={commissions.data?.rows}
      getRowId={(row) => `${row.beneficiaryId}-${row.role}`}
      isLoading={commissions.isPending}
      error={commissions.error}
      onRetry={() => void commissions.refetch()}
      emptyTitleKey="cmp:referrers.commissions_empty"
    />
  );
}
